import { GAME_DAY_MS } from '@dreamgame/shared';
import { computed, signal } from '@preact/signals';
import { markTimeSynced } from './connection';

// ============================================================================
// Time State - Day/Night Cycle
// ============================================================================

// Current game hour (0-24 float)
export const gameHour = signal<number>(12); // Default to noon

// Computed: Is it currently night (8pm - 6am)
export const isNight = computed(() => gameHour.value >= 20 || gameHour.value < 6);

// ============================================================================
// Smooth Time Interpolation (hot-path, no signals)
// ============================================================================

// PERFORMANCE: Mutable state for per-frame interpolation - avoids signal overhead
const GAME_HOUR_PER_MS = 24 / GAME_DAY_MS;

let _smoothHour = 12;
let _serverHour = 12;
let _serverTimestamp = 0;
let _timeSynced = false;

export const getSmoothHour = (): number => _smoothHour;
export const isTimeSynced = (): boolean => _timeSynced;

export const updateSmoothTime = (): void => {
  if (_serverTimestamp === 0) {
    return;
  }
  const elapsed = performance.now() - _serverTimestamp;
  _smoothHour = (_serverHour + elapsed * GAME_HOUR_PER_MS) % 24;
};

// ============================================================================
// Actions
// ============================================================================

export const setGameTime = ({ hour }: { hour: number }): void => {
  gameHour.value = hour;
  _serverHour = hour;
  _serverTimestamp = performance.now();
  if (!_timeSynced) {
    _timeSynced = true;
    markTimeSynced();
  }
};

// ============================================================================
// Helpers
// ============================================================================

// Get formatted time string (e.g., "2:30 PM")
export const getTimeString = (): string => {
  const hour = Math.floor(gameHour.value);
  const minute = Math.floor((gameHour.value % 1) * 60);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

// Get time of day description
export const getTimeOfDay = (): string => {
  const hour = gameHour.value;
  if (hour >= 6 && hour < 12) {
    return 'Morning';
  }
  if (hour >= 12 && hour < 17) {
    return 'Afternoon';
  }
  if (hour >= 17 && hour < 20) {
    return 'Evening';
  }
  return 'Night';
};
